import React, { useEffect } from "react";
import styled from "styled-components";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.main`
  flex: 1;
  max-width: 900px;
  margin: 0 auto;
  padding: 120px 20px 60px;

  @media (max-width: 768px) {
    padding: 100px 16px 40px;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #02495b;
  margin-bottom: 10px;
  font-family: 'Merriweather', serif;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #1CA8CB;
  margin-bottom: 30px;
  font-weight: 500;
`;

const Section = styled.section`
  margin-bottom: 35px;
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  color: #035e75;
  margin-bottom: 15px;
  font-family: 'Merriweather', serif;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: #444;
  line-height: 1.8;
  margin-bottom: 15px;
`;

const List = styled.ul`
  padding-left: 25px;
  margin-bottom: 15px;
`;

const ListItem = styled.li`
  font-size: 1rem;
  color: #444;
  line-height: 1.8;
  margin-bottom: 10px;
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
  border-left: 4px solid #1CA8CB;
  padding: 20px 24px;
  margin: 25px 0;
  border-radius: 0 12px 12px 0;

  h3 {
    color: #02495b;
    font-size: 1.1rem;
    margin-bottom: 10px;
  }

  p {
    margin: 0;
    color: #02495b;
    font-weight: 500;
  }
`;

const Note = styled.div`
  background: #fff8e1;
  border-left: 4px solid #ffc107;
  padding: 15px 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  p {
    margin: 0;
    color: #5d4037;
  }
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: #888;
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #eee;
`;

const ContactSection = styled.div`
  background: #f0f9ff;
  padding: 25px;
  border-radius: 12px;
  margin-top: 30px;

  h3 {
    color: #02495b;
    margin-bottom: 15px;
  }
`;

const CancellationRefund = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer>
      <Navbar />
      <ContentWrapper>
        <Title>Trippro Easy Booking & Refund Policy</Title>
        <Subtitle>Simple, transparent, and traveler-friendly</Subtitle>

        {/* Booking Confirmation */}
        <Section>
          <SectionTitle>📋 Booking Confirmation</SectionTitle>
          <List>
            <ListItem>Your trip is confirmed once <strong>30% advance payment</strong> is received.</ListItem>
            <ListItem>Flights or trains are booked only after <strong>100% flight/train payment</strong>.</ListItem>
            <ListItem>Final hotel, cab, and houseboat bookings are confirmed after advance or full payment.</ListItem>
          </List>
        </Section>

        {/* Land Package Refund */}
        <Section>
          <SectionTitle>🏨 Land Package Refund Policy</SectionTitle>
          <Paragraph><em>(Hotels, Cab, Sightseeing)</em></Paragraph>
          <HighlightBox>
            <h3>✅ 100% Refund Guarantee</h3>
            <p>Cancel up to 7 days before the travel start date and receive a 100% refund of the land package amount.</p>
          </HighlightBox>
          <Note>
            <p><strong>Note:</strong> If you cancel within 7 days of the travel start date, the land package amount will be non-refundable.</p>
          </Note>
        </Section>

        {/* Flights & Trains */}
        <Section>
          <SectionTitle>✈️ Flights & Trains</SectionTitle>
          <List>
            <ListItem>Flight and train cancellations are handled as per the <strong>airline or railway cancellation policy</strong>.</ListItem>
            <ListItem>Trippro does not control flight refund rules set by airlines.</ListItem>
          </List>
        </Section>

        {/* Special Situations */}
        <Section>
          <SectionTitle>⚠️ Special Situations (Natural or External Events)</SectionTitle>
          <List>
            <ListItem>In case of natural calamities, strikes, or government restrictions, refunds depend on hotel, cab, and airline policies.</ListItem>
            <ListItem>Wherever possible, Trippro will assist in <strong>rescheduling the trip</strong> instead of cancellation.</ListItem>
          </List>
        </Section>

        {/* How Refunds Work */}
        <Section>
          <SectionTitle>💰 How Refunds Are Processed</SectionTitle>
          <List>
            <ListItem>Eligible refunds are processed within <strong>7–10 working days</strong>.</ListItem>
            <ListItem>Refunds are made to the <strong>original payment method</strong>.</ListItem>
          </List>
        </Section>

        <Title as="h2" style={{ fontSize: '1.8rem', marginTop: '50px' }}>Terms & Conditions</Title>

        {/* Hotel, Cab & Availability */}
        <Section>
          <SectionTitle>🏪 Hotel, Cab & Availability</SectionTitle>
          <List>
            <ListItem>If any selected hotel, cab, train, or flight is unavailable, we will offer the <strong>best possible similar option</strong>.</ListItem>
            <ListItem>We will always <strong>confirm changes with you</strong> before finalizing.</ListItem>
          </List>
        </Section>

        {/* Support During Trip */}
        <Section>
          <SectionTitle>🤝 Support During the Trip</SectionTitle>
          <List>
            <ListItem>A Trippro advisor will be available before and during the trip for coordination and support.</ListItem>
            <ListItem>We assist only for services clearly mentioned in your confirmed itinerary.</ListItem>
          </List>
        </Section>

        {/* Pricing Transparency */}
        <Section>
          <SectionTitle>💎 Pricing Transparency</SectionTitle>
          <List>
            <ListItem>All major costs are shown upfront in your itinerary.</ListItem>
            <ListItem>Price may change only if:
              <List>
                <ListItem>Travel dates change</ListItem>
                <ListItem>Hotel category changes</ListItem>
                <ListItem>Inclusions are modified</ListItem>
              </List>
            </ListItem>
          </List>
        </Section>

        {/* Important Travel Notes */}
        <Section>
          <SectionTitle>📝 Important Travel Notes</SectionTitle>
          <List>
            <ListItem>Houseboat AC & hot water work as per houseboat rules.</ListItem>
            <ListItem>Hotel check-in / check-out follows hotel policy.</ListItem>
            <ListItem>Cab service is available for 8–9 hours per day.</ListItem>
            <ListItem>Early morning or late-night transfers may include extra charges.</ListItem>
          </List>
        </Section>

        {/* Contact Section */}
        <ContactSection>
          <h3>📞 Need Help?</h3>
          <List>
            <ListItem>📞 Phone: +91 92113 75008</ListItem>
            <ListItem>📧 Email: contact@trippro.in</ListItem>
            <ListItem>💬 WhatsApp: +91 92113 76008</ListItem>
          </List>
        </ContactSection>

        <LastUpdated>Last updated: February 2026</LastUpdated>
      </ContentWrapper>
      <Footer />
    </PageContainer>
  );
};

export default CancellationRefund;
